/**
 * CountryAwareFormatInput — a TextField that reads its placeholder,
 * helper text, and validation regex from the active country's profile
 * pack (`profiles/<cc>/ui_hints/field_formats.json`) via the
 * `/settings/country-metadata` endpoint.
 *
 * Usage:
 *
 *   <CountryAwareFormatInput
 *     country={company.country_code}
 *     field="company_registration_number"
 *     value={form.registration_number}
 *     onChange={(v) => setForm({ ...form, registration_number: v })}
 *   />
 *
 * When the country has no entry for the requested field, the component
 * still renders — it just shows whatever explicit `label` /
 * `placeholder` / `helperText` props were passed. This makes it safe to
 * drop in ahead of profile-pack coverage for a new country.
 */
import { useEffect, useMemo } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { useCountryMetadata } from '@/hooks/useCountryMetadata';

export interface CountryAwareFormatInputProps
  extends Omit<TextFieldProps, 'onChange' | 'value' | 'name'> {
  country: string | null | undefined;
  field: string;
  value: string;
  onChange: (value: string) => void;
  /** Fires whenever validation state changes. Empty value counts as valid. */
  onValidityChange?: (valid: boolean) => void;
  /** When true (default), show the validation error inline on the field. */
  showInlineError?: boolean;
}

export function CountryAwareFormatInput({
  country,
  field,
  value,
  onChange,
  onValidityChange,
  showInlineError = true,
  label,
  placeholder,
  helperText,
  error: externalError,
  ...textFieldProps
}: CountryAwareFormatInputProps) {
  const { data } = useCountryMetadata(country);
  const format = data?.field_formats?.[field];

  const resolvedLabel = label ?? format?.label ?? field;
  const resolvedPlaceholder = placeholder ?? format?.placeholder ?? '';
  const profileHelper = format?.helper_text;

  // Only validate when the user has typed something. An empty field is
  // valid — required-ness is the caller's responsibility.
  const validationError = useMemo<string | null>(() => {
    if (!value || !format?.regex) return null;
    let re: RegExp;
    try {
      re = new RegExp(format.regex);
    } catch {
      return null; // malformed regex in profile — fail open
    }
    if (re.test(value)) return null;
    return format.placeholder
      ? `Expected format: ${format.placeholder}`
      : 'Value does not match the expected format.';
  }, [value, format?.regex, format?.placeholder]);

  useEffect(() => {
    onValidityChange?.(!value || validationError === null);
  }, [value, validationError, onValidityChange]);

  const displayHelper =
    showInlineError && validationError
      ? validationError
      : (helperText as string | undefined) ?? profileHelper ?? '';

  const isError = Boolean(externalError) || (showInlineError && !!validationError);

  return (
    <TextField
      label={resolvedLabel}
      placeholder={resolvedPlaceholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      helperText={displayHelper}
      error={isError}
      {...textFieldProps}
    />
  );
}

export default CountryAwareFormatInput;
